/*
 * UTC offer core: pricing math, gift reconcile, cart-drawer helpers.
 *
 * Loaded by snippets/lp-utc-offer-buybox.liquid (buy box pages) and lazily by
 * assets/_cart-drawer.js when the cart already holds offer or gift lines.
 *
 * Config comes from the buy box root: <div data-uo-cfg='{...json...}'>. It is
 * remembered in sessionStorage so the drawer keeps working on other pages.
 *
 * Gift rules (spec section 5):
 *   anchor = UTC 3 Pack or Protocol 3 Pack on its 3-month selling plan
 *   gifts  = one-time, qty 1, line property _gift = utc3
 *   blister is the gift; Protocol swaps in the pill case only if one is configured; keep the ebook (display-only if no ebook product)
 *   shopper-removed gifts are remembered in sessionStorage and not re-added
 *   free-ness is enforced by the Shopify BXGY discount, never by the theme
 */
(function () {
  'use strict';
  if (window.LPUtcOffer) return;

  var OFFER = 'utc3';
  var CFG_VER = 4;
  var SS_CFG = 'uo_gift_cfg_v4';
  var OLD_KEYS = ['lp_utc_offer_cfg_v1', 'uo_gift_cfg_v1', 'uo_gift_cfg_v2', 'uo_gift_cfg_v3'];
  var SS_REM = 'lp_utc_offer_removed_v1';
  var DEFAULT_HANDLES = { utc: 'urinary-tract-cleanse-protect', brp: 'ph-gut-balance' };
  var SHIPPING_VALUE = 499;

  var cfg = null;
  var busy = null;
  var pending = false;
  var recent = [];
  var timer = null;

  function ssGet(k) { try { return window.sessionStorage.getItem(k); } catch (e) { return null; } }
  function ssSet(k, v) { try { window.sessionStorage.setItem(k, v); } catch (e) { /* storage blocked */ } }

  function loadCfg() {
    OLD_KEYS.forEach(function (k) { try { window.sessionStorage.removeItem(k); } catch (e) { /* storage blocked */ } });
    var el = document.querySelector('[data-uo-cfg]');
    var raw = el ? el.getAttribute('data-uo-cfg') : null;
    var parsed = null;
    if (raw) {
      // the box on the page always wins over storage
      try { parsed = JSON.parse(raw); } catch (e) { parsed = null; }
      if (parsed) { ssSet(SS_CFG, JSON.stringify({ v: CFG_VER, cfg: parsed })); }
    }
    if (!parsed) {
      try {
        var st = JSON.parse(ssGet(SS_CFG) || 'null');
        if (st && st.v === CFG_VER && st.cfg) parsed = st.cfg;
      } catch (e) { parsed = null; }
    }
    cfg = parsed;
    return cfg;
  }

  // gifts are paid products made free by the Fall Reset discount function (via _gift=utc3);
  // only variants in the current page config are ever added
  function hasGift(g) { return !!(g && g.variant); }

  function handles(c) {
    return {
      utc: (c && c.utc && c.utc.handle) || DEFAULT_HANDLES.utc,
      brp: (c && c.brp && c.brp.handle) || DEFAULT_HANDLES.brp
    };
  }

  function prop(item, name) { return item && item.properties ? item.properties[name] : undefined; }
  function isGift(item) { return prop(item, '_gift') === OFFER; }
  function isOfferLine(item) { return prop(item, '_offer') === OFFER; }
  function planName(item) {
    var p = item && item.selling_plan_allocation && item.selling_plan_allocation.selling_plan;
    return (p && p.name) || '';
  }
  function isAnchor(item, c) {
    var h = handles(c);
    if (item.handle !== h.utc && item.handle !== h.brp) return false;
    return /3\s*pack/i.test(item.variant_title || '') && /3\s*month/i.test(planName(item));
  }
  function isOneMonth(item, c) {
    var h = handles(c);
    if (item.handle !== h.utc && item.handle !== h.brp) return false;
    return /1\s*pack/i.test(item.variant_title || '') && /1\s*month/i.test(planName(item));
  }

  /* ---------- pricing (single source of truth for the box and the drawer) ---------- */
  function saleState(c) {
    var off = { on: false, deadline: 0 };
    if (!c || !(c.salePct > 0 || c.protoSalePct > 0)) return off;
    var now = Date.now();
    var endsAt = c.saleEnd ? c.saleEnd * 1000 : 0;
    if (endsAt && endsAt <= now) return off;
    var dl = 0;
    if (c.saleMode === 'rolling' && c.cycleMs > 0 && c.anchor) {
      var anchor = c.anchor * 1000;
      var k = Math.max(0, Math.ceil((now - anchor) / c.cycleMs));
      dl = anchor + k * c.cycleMs;
      if (dl <= now) dl += c.cycleMs;
      if (endsAt && dl > endsAt) dl = endsAt;
    } else if (c.saleMode !== 'off') {
      dl = endsAt;
    }
    return { on: true, deadline: dl };
  }
  function saleOn(c) { return saleState(c).on; }
  function firstOrder(c, kind, k) {
    var p = c && c[kind] && c[kind]['p' + k];
    if (!p) return 0;
    var pct = kind === 'brp' ? c.protoSalePct : c.salePct;
    if (k === '3' && saleOn(c) && pct > 0) return Math.round(p.sub * (100 - pct) / 100);
    return p.sub;
  }
  function pct(strike, first) {
    if (!strike || strike <= 0) return 0;
    return Math.round((1 - first / strike) * 100);
  }
  function money(c, cents) {
    var cur = (c && c.currency) || 'USD';
    try {
      return new Intl.NumberFormat('en-US', { style: 'currency', currency: cur }).format(cents / 100);
    } catch (e) { return '$' + (cents / 100).toFixed(2); }
  }
  // whole-dollar amounts drop the cents ("$20"), otherwise two decimals
  function moneyWhole(c, cents) {
    var r = Math.round(cents);
    if (r % 100 === 0) {
      try {
        return new Intl.NumberFormat('en-US', {
          style: 'currency', currency: (c && c.currency) || 'USD',
          minimumFractionDigits: 0, maximumFractionDigits: 0
        }).format(r / 100);
      } catch (e) { return '$' + (r / 100); }
    }
    return money(c, r);
  }
  function giftCount(c, protocol) {
    var g = (c && c.gifts) || {};
    var first = (protocol && g.pillcase && g.pillcase.variant) || (g.blister && g.blister.variant);
    var ebook = (g.ebook && g.ebook.variant) || g.ebookValue > 0;
    return (first ? 1 : 0) + (ebook ? 1 : 0);
  }

  /* ---------- removed-gift memory ---------- */
  function getRemoved() {
    try { var a = JSON.parse(ssGet(SS_REM) || '[]'); return Array.isArray(a) ? a : []; } catch (e) { return []; }
  }
  function markRemoved(item) {
    if (!item || !item.variant_id) return;
    var list = getRemoved();
    var id = String(item.variant_id);
    if (list.indexOf(id) < 0) { list.push(id); ssSet(SS_REM, JSON.stringify(list)); }
  }

  /* ---------- cart plumbing ---------- */
  function post(url, body) {
    return fetch(url, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify(body)
    }).then(function (res) {
      return res.json().catch(function () { return null; }).then(function (data) {
        if (!res.ok) {
          var err = new Error(url + ' ' + res.status);
          err.description = (data && (data.description || data.message)) || 'We could not update your cart. Please try again.';
          throw err;
        }
        return data;
      });
    });
  }
  function fire(name) { document.dispatchEvent(new Event(name)); }

  function wantedGifts(c, protocol) {
    var want = [];
    var g = (c && c.gifts) || {};
    var removed = getRemoved();
    if (protocol && hasGift(g.pillcase)) {
      want.push({ variant: g.pillcase.variant, kind: 'case' });
    } else if (hasGift(g.blister)) {
      want.push({ variant: g.blister.variant, kind: 'blister' });
    }
    if (hasGift(g.ebook)) want.push({ variant: g.ebook.variant, kind: 'ebook' });
    return want.filter(function (w) { return removed.indexOf(String(w.variant)) < 0; });
  }

  function apply(cart) {
    var items = cart.items || [];
    var c = loadCfg();
    var gifts = items.filter(isGift);
    var hasOffer = items.some(isOfferLine);
    // Variant arm only: no offer line, no gift line, no page flag means hands off.
    if (!window.__LP_UTC_OFFER_ACTIVE && !hasOffer && !gifts.length) return Promise.resolve(false);

    var anchor = items.filter(function (i) { return isAnchor(i, c); })[0];
    var want = [];
    if (anchor) {
      // Without config we cannot know the gift variants, so leave the cart alone.
      if (!c) return Promise.resolve(false);
      want = wantedGifts(c, anchor.handle === handles(c).brp);
    }

    var updates = {};
    var hasUpdates = false;
    var kept = {};
    gifts.forEach(function (it) {
      var match = want.filter(function (w) { return w.variant === it.variant_id; })[0];
      if (!match || kept[it.variant_id] || it.selling_plan_allocation) {
        updates[it.key] = 0;
        hasUpdates = true;
      } else {
        kept[it.variant_id] = true;
        if (it.quantity !== 1) { updates[it.key] = 1; hasUpdates = true; }
      }
    });
    var toAdd = want.filter(function (w) { return !kept[w.variant]; });

    var chain = Promise.resolve();
    if (hasUpdates) chain = chain.then(function () { return post('/cart/update.js', { updates: updates }); });
    if (toAdd.length) {
      chain = chain.then(function () {
        return post('/cart/add.js', {
          items: toAdd.map(function (w) {
            return { id: w.variant, quantity: 1, properties: { _gift: OFFER, _gift_kind: w.kind } };
          })
        });
      });
    }
    return chain.then(function () { return hasUpdates || toAdd.length > 0; });
  }

  // opts.silent: skip the cart:modified event (the caller fires it).
  function reconcile(opts) {
    if (busy) { pending = true; return busy; }
    var now = Date.now();
    recent = recent.filter(function (t) { return now - t < 10000; });
    if (recent.length >= 4) return Promise.resolve(false); // runaway guard
    busy = fetch('/cart.js', { credentials: 'same-origin' })
      .then(function (res) { return res.json(); })
      .then(apply)
      .then(function (changed) {
        if (changed) {
          recent.push(Date.now());
          if (!(opts && opts.silent)) fire('cart:modified');
        }
        return changed;
      })
      .catch(function (e) {
        // surface a validation message (422) to the drawer instead of failing silently
        document.dispatchEvent(new CustomEvent('lp-utc-offer:error', {
          detail: { message: (e && e.description) || 'We could not update your cart. Please try again.' }
        }));
        return false;
      })
      .then(function (changed) {
        busy = null;
        if (pending) { pending = false; setTimeout(function () { reconcile(); }, 0); }
        return changed;
      });
    return busy;
  }
  function schedule() {
    clearTimeout(timer);
    timer = setTimeout(function () { reconcile(); }, 60);
  }

  /* ---------- drawer helpers ---------- */
  function savings(cart) {
    var c = loadCfg();
    var items = cart.items || [];
    var sub = false;
    var value = 0;
    items.forEach(function (i) {
      if (i.selling_plan_allocation) sub = true;
      var unit = c && c.values ? c.values[String(i.variant_id)] : null;
      if (unit == null) unit = i.original_price;
      value += unit * i.quantity;
    });
    if (!sub) return 0;
    value += SHIPPING_VALUE;
    return Math.max(0, value - cart.total_price);
  }

  function lineValue(item) {
    var c = loadCfg();
    var unit = c && c.values ? c.values[String(item.variant_id)] : null;
    if (unit == null) unit = item.original_price;
    return unit * item.quantity;
  }

  function nudge(cart) {
    var c = loadCfg();
    if (!c) return null;
    var line = (cart.items || []).filter(function (i) {
      return isOneMonth(i, c) && (isOfferLine(i) || window.__LP_UTC_OFFER_ACTIVE);
    })[0];
    if (!line) return null;
    var protocol = line.handle === handles(c).brp;
    var kind = protocol ? 'brp' : 'utc';
    if (!c[kind] || !c[kind].p3) return null;
    var first3 = firstOrder(c, kind, '3');
    var n = giftCount(c, protocol);
    return {
      key: line.key,
      text: 'Switch to 3 months and pay ' + moneyWhole(c, first3 / 3) + ' a month instead of ' +
        money(c, line.final_price) + ', plus ' + n + ' free gift' + (n === 1 ? '' : 's') + '.'
    };
  }

  function switchTo3(key) {
    return fetch('/cart.js', { credentials: 'same-origin' })
      .then(function (res) { return res.json(); })
      .then(function (cart) {
        var c = loadCfg();
        var line = (cart.items || []).filter(function (i) { return i.key === key; })[0];
        if (!line || !c) throw new Error('switch: missing line or config');
        var kind = line.handle === handles(c).brp ? 'brp' : 'utc';
        var p3 = c[kind] && c[kind].p3;
        if (!p3) throw new Error('switch: no 3 pack');
        // add the 3 Pack (anchor) first, with its gifts in the same call, so a failed add
        // never empties the cart and gifts never exist without an anchor. The 1-month
        // line is not an anchor, so removing it afterwards is always valid.
        var items = [{ id: p3.variant, quantity: 1, selling_plan: p3.plan, properties: { _offer: OFFER } }];
        wantedGifts(c, kind === 'brp').forEach(function (w) {
          items.push({ id: w.variant, quantity: 1, properties: { _gift: OFFER, _gift_kind: w.kind } });
        });
        return post('/cart/add.js', { items: items })
          .then(function () { return post('/cart/update.js', { updates: (function () { var u = {}; u[key] = 0; return u; })() }); });
      })
      .then(function () { return reconcile({ silent: true }); })
      .then(function () { fire('cart:modified'); });
  }

  function renewal(cart) {
    var c = loadCfg();
    var line = (cart.items || []).filter(function (i) { return !isGift(i) && i.selling_plan_allocation; })[0];
    if (!line) return '';
    var a = line.selling_plan_allocation;
    var cents = a.per_delivery_price != null ? a.per_delivery_price : a.price;
    // Only the "every N months" part of the plan name, so marketing text added
    // to a plan name in the subscription app doesn't leak into this sentence.
    var pn = a.selling_plan && a.selling_plan.name ? a.selling_plan.name : '';
    var pm = pn.match(/(\d+)\s*-?\s*months?\b/i);
    var cadence = pm ? (+pm[1] === 1 ? 'every month' : 'every ' + pm[1] + ' months') : pn.toLowerCase();
    return 'Then ' + money(c, cents) + (cadence ? ' ' + cadence : '') + '. Skip, pause or cancel anytime.';
  }

  window.LPUtcOffer = {
    version: 1,
    offerTag: OFFER,
    shippingValue: SHIPPING_VALUE,
    reconcile: reconcile,
    isGift: isGift,
    isOfferLine: isOfferLine,
    markRemoved: markRemoved,
    savings: savings,
    lineValue: lineValue,
    nudge: nudge,
    switchTo3: switchTo3,
    renewal: renewal,
    price: {
      saleOn: saleOn,
      saleState: saleState,
      firstOrder: firstOrder,
      pct: pct,
      money: money,
      moneyWhole: moneyWhole,
      giftCount: giftCount
    }
  };

  document.addEventListener('cart:modified', schedule);
  document.addEventListener('cart:updated', schedule);
  document.dispatchEvent(new CustomEvent('lp-utc-offer:ready'));
  schedule();
})();
